// src/pages/partners/Partners.tsx
//
// Partners page
// - Uses PartnersSearchPanel (shared SearchPanel wrapper).
// - Keeps URL query params in sync with the *applied* search state for shareable deep links.
// - Maintains draft vs applied search states.
// - Auto-search behavior is controlled by SearchPanel via `autoSearch` prop.
//
// Table refactor policy:
// - Replace legacy summary + go-to-page UI with StandardTableToolbar.
// - Use StandardTableFrame + StandardPagination.
// - Navigation rule: DO NOT navigate on row click. Navigate ONLY when clicking Partner Name.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  BlockOutlined,
  CheckCircleOutline,
  DeleteOutline,
  ErrorOutline,
  PauseCircleOutline,
  ReportProblemOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePartnerList } from '@queries/partners/usePartnerList';
import { usePartnerCount } from '@queries/partners/usePartnerCount';

import { filterSearchParams } from '@utils/search';
import { timestampToDate } from '@utils/time';

import type { PartnersSearchForm, PartnerStatus } from '@models/partner';

import { useListReturnNavigation } from '@hooks/useListReturnNavigation';
import { usePageHeader } from '@components/common/pageHeader.context';
import PartnersSearchPanel from '@components/partner/PartnersSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';

import {
  StandardTableToolbar,
  StandardTableFrame,
  StandardPagination,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;
const BALANCE_LOW_THRESHOLD = 1000;
const BALANCE_CRITICAL_THRESHOLD = 500;

/**
 * Page-level policy:
 * - Global autoSearch default is OFF (SearchPanel constants).
 * - Enable autoSearch only for this page if desired.
 */
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const EMPTY_FORM: PartnersSearchForm = {
  name: '',
  manager: '',
  email: '',
  address: '',
};

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const readFormFromUrl = (sp: URLSearchParams): PartnersSearchForm => ({
  name: sp.get('name') || '',
  manager: sp.get('manager') || '',
  email: sp.get('email') || '',
  address: sp.get('address') || '',
});

/**
 * Normalize a search form:
 * - Trim strings
 * - Drop empty values
 */
const cleanSearchForm = (form: PartnersSearchForm): PartnersSearchForm => {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(form)) {
    if (value === undefined || value === null) continue;

    if (typeof value === 'string') {
      const v = value.trim();
      if (!v) continue;
      cleaned[key] = v;
      continue;
    }

    cleaned[key] = value;
  }

  return cleaned as PartnersSearchForm;
};

const buildUrlParams = (form: PartnersSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);
  const stringParams: Record<string, string> = {};

  for (const [k, v] of Object.entries(cleaned)) {
    stringParams[k] = String(v);
  }

  return {
    ...filterSearchParams(stringParams),
    page: String(page),
  };
};

const formsEqual = (a: PartnersSearchForm, b: PartnersSearchForm) =>
  (a.name ?? '') === (b.name ?? '') &&
  (a.manager ?? '') === (b.manager ?? '') &&
  (a.email ?? '') === (b.email ?? '') &&
  (a.address ?? '') === (b.address ?? '');

const USD_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toUsdNumberDisplay = (amount: unknown) => {
  const value = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(value)) return '0.00';
  return USD_NUMBER_FORMATTER.format(value);
};

const getPartnerStatusMeta = (status?: PartnerStatus | string | null) => {
  switch (status) {
    case 'INACTIVE':
      return {
        label: 'Inactive',
        color: 'warning.main',
        icon: <PauseCircleOutline fontSize="small" />,
      };
    case 'DELETED':
      return {
        label: 'Deleted',
        color: 'text.disabled',
        icon: <BlockOutlined fontSize="small" />,
      };
    case 'ACTIVE':
    default:
      return {
        label: 'Active',
        color: 'success.main',
        icon: <CheckCircleOutline fontSize="small" />,
      };
  }
};

const getBalanceMeta = (balance: unknown) => {
  const value = typeof balance === 'number' ? balance : Number(balance);
  const safeValue = Number.isFinite(value) ? value : 0;

  if (safeValue <= 0) {
    return {
      label: 'Balance error',
      description: 'Balance is zero or negative.',
      color: 'error.main',
      icon: <ErrorOutline fontSize="small" />,
    };
  }

  if (safeValue <= BALANCE_CRITICAL_THRESHOLD) {
    return {
      label: 'Critical balance',
      description: `Balance is at or below USD ${toUsdNumberDisplay(BALANCE_CRITICAL_THRESHOLD)}.`,
      color: 'error.dark',
      icon: <ReportProblemOutlined fontSize="small" />,
    };
  }

  if (safeValue <= BALANCE_LOW_THRESHOLD) {
    return {
      label: 'Low balance',
      description: `Balance is at or below USD ${toUsdNumberDisplay(BALANCE_LOW_THRESHOLD)}.`,
      color: 'warning.main',
      icon: <WarningAmberOutlined fontSize="small" />,
    };
  }

  return null;
};

const Partners = () => {
  const navigate = useNavigate();
  const { navigateFromList } = useListReturnNavigation();
  const [searchParams, setSearchParams] = useSearchParams();

  /* ------------------------------------------------------------------ */
  /* Page header slots                                                   */
  /* ------------------------------------------------------------------ */

  const { setPageHeaderSlots } = usePageHeader();

  const handleAddPartner = useCallback(() => {
    navigate('/partners/add');
  }, [navigate]);

  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add-partner',
          label: 'Add Partner',
          startIcon: <Add />,
          onClick: handleAddPartner,
          primary: true,
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

    // Clear header slots on unmount to avoid leaking actions into other pages.
    return () => setPageHeaderSlots({ actions: [] });
  }, [handleAddPartner, setPageHeaderSlots]);

  /* ------------------------------------------------------------------ */
  /* Search state (draft vs applied)                                     */
  /* ------------------------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState<PartnersSearchForm>(() => readFormFromUrl(searchParams));
  const [appliedSearch, setAppliedSearch] = useState<PartnersSearchForm>(() =>
    cleanSearchForm(readFormFromUrl(searchParams)),
  );

  /* ------------------------------------------------------------------ */
  /* Pagination                                                         */
  /* ------------------------------------------------------------------ */

  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  // Filter panel accordion open state (controlled).
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [includeDeleted, setIncludeDeleted] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Sync local state from URL (back/forward / deep links)               */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const nextDraft = readFormFromUrl(searchParams);
    const nextApplied = cleanSearchForm(nextDraft);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setDraftSearch((prev) => (formsEqual(prev, nextDraft) ? prev : nextDraft));
    setAppliedSearch((prev) => (formsEqual(prev, nextApplied) ? prev : nextApplied));
    setPage((prev) => (prev === nextPage ? prev : nextPage));
  }, [searchParams]);

  /* ------------------------------------------------------------------ */
  /* URL sync helper (based on applied)                                  */
  /* ------------------------------------------------------------------ */

  const syncUrlFromApplied = useCallback(
    (nextApplied: PartnersSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  /* ------------------------------------------------------------------ */
  /* Draft setter used by inputs/chips                                   */
  /* ------------------------------------------------------------------ */

  const setValue: SetSearchValueFn<PartnersSearchForm> = useCallback((key, value) => {
    // Keep empty strings in draft; normalize at apply time.
    setDraftSearch((prev) => ({
      ...prev,
      [key]: ((value ?? '') as unknown) as PartnersSearchForm[keyof PartnersSearchForm],
    }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Apply / Reset                                                       */
  /* ------------------------------------------------------------------ */

  const handleSearch = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);

    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied]);

  const handleReset = useCallback(() => {
    setDraftSearch(EMPTY_FORM);

    const nextApplied = cleanSearchForm(EMPTY_FORM);

    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [syncUrlFromApplied]);

  /* ------------------------------------------------------------------ */
  /* Queries (MUST use applied, not draft)                               */
  /* ------------------------------------------------------------------ */

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const { data: listData, isLoading: isLoadingList } = usePartnerList({
    page,
    pageSize: PAGE_SIZE,
    searchParam: cleanedApplied,
    includeDeleted,
  });

  const { data: totalCount, isLoading: isLoadingCount } = usePartnerCount(
    cleanedApplied,
    includeDeleted,
  );

  const rows = useMemo(() => listData?.data ?? [], [listData]);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE)), [safeTotalCount]);

  // Safety: clamp current page if count changes.
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  const isTableLoading = Boolean(isLoadingList || isLoadingCount);

  /* ------------------------------------------------------------------ */
  /* Paging handlers                                                     */
  /* ------------------------------------------------------------------ */

  const handleChangePage = (next: number) => {
    const value = clamp(next, 1, totalPages);
    setPage(value);
    syncUrlFromApplied(appliedSearch, value);
  };

  /* ------------------------------------------------------------------ */
  /* Table                                                               */
  /* ------------------------------------------------------------------ */

  const columnsCount = 8;

  const handleNavigateDetail = useCallback(
    (partnerId: number) => {
      if (!Number.isFinite(partnerId) || partnerId <= 0) return;
      navigateFromList(`/partners/${partnerId}`);
    },
    [navigateFromList],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PartnersSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchForm={draftSearch}
        setValue={setValue}
        onSearch={handleSearch}
        onReset={handleReset}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      <Box mt={1.5} display="flex" justifyContent="flex-end">
        <Tooltip title="Deleted partners are hidden by default.">
          <FormControlLabel
            labelPlacement="start"
            control={
              <Switch
                checked={includeDeleted}
                onChange={(e) => {
                  setIncludeDeleted(e.target.checked);
                  setPage(1);
                }}
                size="small"
              />
            }
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <DeleteOutline sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight={600}>
                  Show deleted partners
                </Typography>
              </Stack>
            }
            sx={(theme) => ({
              m: 0,
              color: includeDeleted ? 'primary.main' : 'text.secondary',
              transition: theme.transitions.create(['color'], {
                duration: theme.transitions.duration.shortest,
              }),
              '& .MuiFormControlLabel-label': {
                display: 'flex',
              },
              '& .MuiSwitch-root': {
                ml: 0.5,
              },
            })}
          />
        </Tooltip>
      </Box>

      {/* Standard toolbar (Total + Page Jump) */}
      <Box mt={2} />
      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <Paper
          sx={(theme) => ({
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          })}
        >
          {isTableLoading ? <LinearProgress /> : null}

          <StandardTableFrame<any>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell>Partner Name</TableCell>
                <TableCell align="right">Balance (USD)</TableCell>
                <TableCell>Account Manager</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell align="center">Created</TableCell>
              </TableRow>
            }
            rows={rows}
            getRowKey={(row) => String(row.partnerId)}
            columnsCount={columnsCount}
            isLoading={isTableLoading}
            loadingVariant="skeleton"
            skeletonRows={8}
            emptyText="No partners found."
            minWidth={1080}
            renderRow={(row, index) => {
              const partnerId = Number(row.partnerId);
              const canNavigate = Number.isFinite(partnerId) && partnerId > 0;
              const isDeleted = row.status === 'DELETED';
              const statusMeta = getPartnerStatusMeta(row.status);
              const balanceMeta = getBalanceMeta(row.balance);

              return (
                <React.Fragment>
                  <TableCell
                    align="right"
                    sx={{
                      ...cellSx.number,
                      opacity: isDeleted ? 0.5 : 1,
                    }}
                  >
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </TableCell>

                  <TableCell sx={{ opacity: isDeleted ? 0.5 : 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Tooltip title={statusMeta.label}>
                        <Box
                          component="span"
                          sx={{
                            width: 20,
                            height: 20,
                            flex: '0 0 20px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: statusMeta.color,
                          }}
                        >
                          {statusMeta.icon}
                        </Box>
                      </Tooltip>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          if (!canNavigate) return;
                          handleNavigateDetail(partnerId);
                        }}
                        sx={{
                          minWidth: 0,
                          border: 0,
                          p: 0,
                          bgcolor: 'transparent',
                          cursor: canNavigate ? 'pointer' : 'default',
                          color: canNavigate ? 'primary.main' : 'text.primary',
                          font: 'inherit',
                          fontWeight: 700,
                          textAlign: 'left',
                          textDecoration: canNavigate ? 'underline' : 'none',
                          textUnderlineOffset: '3px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '&:hover': canNavigate ? { color: 'primary.dark' } : undefined,
                          '&:focus-visible': canNavigate
                            ? {
                                outline: '2px solid',
                                outlineColor: 'primary.main',
                                outlineOffset: 2,
                                borderRadius: 0.5,
                              }
                            : undefined,
                        }}
                      >
                        {row.name ?? '-'}
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ ...cellSx.money, opacity: isDeleted ? 0.5 : 1 }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      {balanceMeta && (
                        <Tooltip title={balanceMeta.description}>
                          <Box
                            component="span"
                            sx={{
                              width: 18,
                              height: 18,
                              flex: '0 0 18px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: balanceMeta.color,
                            }}
                          >
                            {balanceMeta.icon}
                          </Box>
                        </Tooltip>
                      )}
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight={balanceMeta ? 800 : 500}
                        sx={{
                          color: balanceMeta?.color ?? 'text.primary',
                          fontFamily: 'inherit',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {toUsdNumberDisplay(row.balance)}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ opacity: isDeleted ? 0.5 : 1 }}>{row.managerName ?? '-'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13, opacity: isDeleted ? 0.5 : 1 }}>
                    {row.managerEmail ?? '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx.nowrap,
                      color: 'text.secondary',
                      fontSize: 13,
                      opacity: isDeleted ? 0.5 : 1,
                    }}
                  >
                    {row.managerTelephone ?? '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 360,
                      minWidth: 220,
                      color: 'text.secondary',
                      fontSize: 13,
                      opacity: isDeleted ? 0.5 : 1,
                    }}
                  >
                    <Tooltip title={row.address || ''} disableHoverListener={!row.address}>
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.address ?? '-'}
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 13, opacity: isDeleted ? 0.5 : 1 }}
                  >
                    {timestampToDate(row.createdAt)}
                  </TableCell>
                </React.Fragment>
              );
            }}
          />
        </Paper>
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Partners;
